import { useSyncExternalStore } from 'react';
import { PRIMARY_TOUCH_QUERY, hasPrimaryTouch } from '../utils/deviceDetection';

interface DeviceClass {
  /** True se o input primário é touch (sem hover + pointer coarse) — alinha com CSS @media */
  isTouch: boolean;
  /** True se a largura está entre 768px e 1366px (iPad range) */
  isTabletSize: boolean;
  /** True se orientação é landscape */
  isLandscape: boolean;
}

const TABLET_QUERY = '(min-width: 768px) and (max-width: 1366px)';
const LANDSCAPE_QUERY = '(orientation: landscape)';

// Singleton — evita criar múltiplos listeners de matchMedia
let cachedState: DeviceClass | null = null;
let listeners = new Set<() => void>();

function getSnapshot(): DeviceClass {
  if (cachedState) return cachedState;
  return computeState();
}

function getServerSnapshot(): DeviceClass {
  return { isTouch: false, isTabletSize: false, isLandscape: false };
}

function computeState(): DeviceClass {
  if (typeof window === 'undefined') {
    return { isTouch: false, isTabletSize: false, isLandscape: false };
  }
  cachedState = {
    isTouch: hasPrimaryTouch(),
    isTabletSize: window.matchMedia(TABLET_QUERY).matches,
    isLandscape: window.matchMedia(LANDSCAPE_QUERY).matches,
  };
  return cachedState;
}

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};

  // Registrar listener
  listeners.add(callback);

  // Criar media query listeners apenas uma vez
  if (listeners.size === 1) {
    const touchMql = window.matchMedia(PRIMARY_TOUCH_QUERY);
    const tabletMql = window.matchMedia(TABLET_QUERY);
    const landscapeMql = window.matchMedia(LANDSCAPE_QUERY);

    const onChange = () => {
      cachedState = null; // Invalidar cache
      computeState();
      listeners.forEach((cb) => cb());
    };

    touchMql.addEventListener('change', onChange);
    tabletMql.addEventListener('change', onChange);
    landscapeMql.addEventListener('change', onChange);

    // Cleanup ao remover último listener
    return () => {
      listeners.delete(callback);
      if (listeners.size === 0) {
        touchMql.removeEventListener('change', onChange);
        tabletMql.removeEventListener('change', onChange);
        landscapeMql.removeEventListener('change', onChange);
        cachedState = null;
      }
    };
  }

  return () => {
    listeners.delete(callback);
  };
}

/**
 * Hook reativo para classificação de dispositivo.
 *
 * Usa matchMedia com listeners — re-render automático em mudança de
 * orientação ou resize que cruze breakpoints.
 *
 * isTouch alinha com a semântica CSS: `(hover: none) and (pointer: coarse)`.
 * Dell touchscreen com trackpad → isTouch=false (hover detectado, pointer fine).
 * iPad real → isTouch=true (sem hover, pointer coarse).
 *
 * ```tsx
 * const { isTouch, isTabletSize, isLandscape } = useDeviceClass();
 * if (isTabletSize && !isLandscape) {
 *   // iPad portrait — sidebar como overlay
 * }
 * ```
 */
export function useDeviceClass(): DeviceClass {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Telemetria: detectar combinação suspeita (tablet em tela > 1280px)
  const isTablet = state.isTouch && state.isTabletSize;
  if (isTablet && typeof window !== 'undefined' && window.innerWidth > 1280) {
    console.warn('[useDeviceClass] Suspicious combination:', {
      isTablet, innerWidth: window.innerWidth
    });
  }

  return state;
}
